/**
 * Chart geometry for the report. Pure functions: numbers in, numbers and path
 * strings out, so the shapes are tested without a DOM. The components only lay
 * out what these return.
 */

const round = (value: number, places = 2) => Number(value.toFixed(places));

/** Where `value` falls between `min` and `max`, as a percentage, clamped so a
 * value outside the scale still renders on it rather than off it. */
export function positionOf(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

/** A bar's height as a percentage of the axis, clamped to 0-100. */
export function barHeightPercent(score: number, outOf: number): number {
  return round(positionOf(score, 0, outOf));
}

/** Value-axis labels, top down: 100, 80 … 0 for outOf=100, divisions=5. */
export function axisTicks(outOf: number, divisions: number): number[] {
  return Array.from({ length: divisions + 1 }, (_, i) => outOf - (i * outOf) / divisions);
}

export interface RadarLabel {
  x: number;
  y: number;
  /** The domain name split into words, one per line. */
  lines: string[];
  score: number;
  /** Baseline offsets, so a wrapped name stays centred on its anchor. */
  labelDy: number;
  scoreDy: number;
  /** The name's offset when it is drawn without a score under it, centring
   * the name block alone on the anchor. */
  nameDy: number;
}

export interface RadarLayout {
  width: number;
  height: number;
  /** One vertex per domain on the score polygon. */
  points: { x: number; y: number }[];
  /** The concentric polygonal web as SVG `points`, outermost last. */
  rings: string[];
  /** Centre to each outer vertex. */
  spokes: { x: number; y: number }[];
  labels: RadarLabel[];
}

/** The drawing's own units. The SVG scales it, so these are not pixels. */
const RADAR = { width: 356, height: 362, radius: 94, labelOffset: 40, lineHeight: 14, rings: 4 };

export interface RadarGeometry {
  width: number;
  height: number;
  radius: number;
  /** How far beyond the outer ring each label's anchor sits. */
  labelOffset: number;
  lineHeight: number;
  rings: number;
  /** Stretches label anchors sideways, so a wide name beside the web clears it. 1 is none. */
  spreadX: number;
  /**
   * Centres each name and its score as one block on the anchor, the score `scoreGap` below the
   * name's last line. Without it, the report's offsets apply.
   */
  scoreGap?: number;
}

/**
 * A straight-edged radar: the first domain at twelve o'clock, then clockwise.
 * Fewer than three domains cannot make a polygon, so the caller decides not to
 * draw one; this still returns something well-formed rather than throwing.
 * `geometry` overrides the report's proportions, for a radar drawn elsewhere.
 */
export function radarLayout(
  domains: { label: string; score: number }[],
  outOf: number,
  geometry: Partial<RadarGeometry> = {}
): RadarLayout {
  const { width, height, radius, labelOffset, lineHeight, rings: ringCount, spreadX, scoreGap } = {
    ...RADAR,
    spreadX: 1,
    ...geometry,
  };
  const cx = width / 2;
  const cy = height / 2;
  const n = Math.max(domains.length, 1);
  const angle = (i: number) => (i * 2 * Math.PI) / n - Math.PI / 2;
  const at = (i: number, r: number) => ({
    x: round(cx + r * Math.cos(angle(i))),
    y: round(cy + r * Math.sin(angle(i))),
  });
  const polygon = (r: number) =>
    domains
      .map((_, i) => at(i, r))
      .map((p) => `${p.x},${p.y}`)
      .join(" ");

  return {
    width,
    height,
    points: domains.map((d, i) => at(i, (positionOf(d.score, 0, outOf) / 100) * radius)),
    rings: Array.from({ length: ringCount }, (_, k) => polygon((radius * (k + 1)) / ringCount)),
    spokes: domains.map((_, i) => at(i, radius)),
    labels: domains.map((d, i) => {
      const anchor = at(i, radius + labelOffset);
      const x = round(cx + (anchor.x - cx) * spreadX);
      const y = anchor.y;
      const lines = d.label.split(" ");
      const block = (lines.length - 1) * lineHeight;
      const wrapped = lines.length > 1;
      if (scoreGap !== undefined) {
        const labelDy = round(lineHeight / 3 - (block + scoreGap) / 2);
        return {
          x,
          y,
          lines,
          score: d.score,
          labelDy,
          scoreDy: round(labelDy + block + scoreGap),
          nameDy: lineHeight / 3 - block / 2,
        };
      }
      return {
        x,
        y,
        lines,
        score: d.score,
        labelDy: wrapped ? -8 - block / 2 : -4,
        scoreDy: wrapped ? 24 + block / 2 : 18,
        // A third of the line height takes a baseline to the middle of a line.
        nameDy: lineHeight / 3 - block / 2,
      };
    }),
  };
}

/** How far each control point reaches towards its neighbours. A sixth is the
 * standard Catmull-Rom spacing, which passes exactly through every point. */
const SMOOTHING = 1 / 6;

/**
 * A curve through measured points rather than a line between them. Control
 * points are clamped to the plot: unclamped, a spline overshoots a sharp peak
 * and dips below the baseline, drawing negative density that was never measured.
 */
export function smoothPath(plotted: { x: number; y: number }[], height: number): string | null {
  if (plotted.length === 0) return null;
  const clamp = (y: number) => Math.min(height, Math.max(0, y));
  // The index is clamped into range, and the list is non-empty (checked above).
  const at = (i: number) => plotted[Math.min(plotted.length - 1, Math.max(0, i))] as { x: number; y: number };

  const origin = at(0);
  let path = `M ${round(origin.x)},${round(origin.y)}`;
  for (let i = 0; i < plotted.length - 1; i += 1) {
    const before = at(i - 1);
    const start = at(i);
    const end = at(i + 1);
    const after = at(i + 2);
    const c1x = start.x + (end.x - before.x) * SMOOTHING;
    const c1y = clamp(start.y + (end.y - before.y) * SMOOTHING);
    const c2x = end.x - (after.x - start.x) * SMOOTHING;
    const c2y = clamp(end.y - (after.y - start.y) * SMOOTHING);
    path += ` C ${round(c1x)},${round(c1y)} ${round(c2x)},${round(c2y)} ${round(end.x)},${round(end.y)}`;
  }
  return path;
}

export interface DistributionPoint {
  score: number;
  /** Relative: counts, proportions and percentages all plot the same. */
  density: number;
}

/** Below this a distribution is a point or a line, not a shape worth drawing. */
export const MIN_DISTRIBUTION_POINTS = 3;

export interface DistributionPlot {
  /** Closed path, ready for a `<path d>` in a `0 0 width height` viewBox. */
  path: string;
  /** Where the curve is at a score, in viewBox y (0 = top). */
  curveYAt: (score: number) => number;
}

/**
 * The lowest overall score the index can give. The curve's taper stays above
 * it, so the chart never suggests anyone scored lower.
 */
const INDEX_FLOOR = 20;

/**
 * The measured buckets with an empty one added beyond each end, so the curve
 * meets the baseline there rather than stopping in mid-air. The bucket width
 * is read off the data, as the narrowest gap between two buckets. The added
 * points stay within the scores the index can give; where the data already
 * reaches that limit the curve drops straight to the baseline there instead.
 */
function withTapers(sorted: DistributionPoint[], outOf: number): DistributionPoint[] {
  const first = sorted[0]?.score ?? 0;
  const last = sorted[sorted.length - 1]?.score ?? 0;
  const bucketWidth = Math.min(...sorted.slice(1).map((p, i) => p.score - (sorted[i]?.score ?? p.score)));
  if (!(bucketWidth > 0)) return sorted;

  const below = Math.max(Math.min(INDEX_FLOOR, first), first - bucketWidth);
  const above = Math.min(Math.max(outOf, last), last + bucketWidth);
  return [
    ...(below < first ? [{ score: below, density: 0 }] : []),
    ...sorted,
    ...(above > last ? [{ score: above, density: 0 }] : []),
  ];
}

/**
 * The population curve. Only ever drawn from points the caller measured — a
 * shape computed from the learner's score and the average would be
 * indistinguishable from an observed one. Returns null when there is nothing
 * honest to draw.
 */
export function plotDistribution(
  distribution: DistributionPoint[] | undefined,
  outOf: number,
  width: number,
  height: number
): DistributionPlot | null {
  if (!distribution || distribution.length < MIN_DISTRIBUTION_POINTS) return null;
  const sorted = [...distribution].sort((a, b) => a.score - b.score);
  // The tallest observed bucket reaches the top of the plot.
  const peak = Math.max(...sorted.map((p) => Math.max(0, p.density)));
  if (peak <= 0) return null;

  const toX = (score: number) => (positionOf(score, 0, outOf) / 100) * width;
  // The curve ends where the data does: each end tapers to the baseline one
  // bucket beyond the last measured one, where the next bucket out would
  // have sat had anyone landed in it.
  const plotted = withTapers(sorted, outOf).map((p) => ({
    x: toX(p.score),
    y: height - (Math.max(0, p.density) / peak) * height,
  }));
  const path = smoothPath(plotted, height);
  const first = plotted[0];
  const last = plotted[plotted.length - 1];
  if (!path || !first || !last) return null;

  // Interpolated between the two buckets either side — the line the path
  // draws between them — so a marker sits on the curve, not near it.
  const curveYAt = (score: number): number => {
    const x = toX(score);
    const after = plotted.findIndex((p) => p.x >= x);
    if (after === -1) return last.y;
    if (after === 0) return first.y;
    const before = plotted[after - 1];
    const next = plotted[after];
    if (!before || !next) return last.y;
    const span = next.x - before.x;
    if (span === 0) return next.y;
    return before.y + ((x - before.x) / span) * (next.y - before.y);
  };

  // Closed along the baseline between the curve's own ends: closed along the
  // whole axis, the fill drew a wedge over scores nobody has.
  return { path: `${path} L ${round(last.x)},${height} L ${round(first.x)},${height} Z`, curveYAt };
}

/** A marker's height above the axis, as a percentage of the plot. */
export function markerHeightPercent(curveY: number, height: number): number {
  return round(((height - curveY) / height) * 100);
}

/** Tones a band can carry. The caller decides which scores are bad news. */
export type ScoreBandTone = "critical" | "low" | "moderate" | "neutral" | "good";

export interface ScaleColumn {
  /** Where the band's column starts along the scale, as a percentage. */
  start: number;
  /** How much of the scale the column takes, as a percentage. */
  span: number;
}

/**
 * Each band's column along the range scale, and where a value lands on it.
 *
 * Columns take the bands' `width`s when every band has one (the designs do not
 * draw the bands to scale, so a narrow band keeps room for its name), and
 * their score ranges otherwise. A value is placed piecewise: within the column
 * of the band it falls in, in proportion to where it sits between that band's
 * bounds, so the marker, the boundaries and the colours stay on the right
 * score however wide each column is drawn. With columns sized by score range
 * that is the plain linear scale. A value outside the bands is clamped to the
 * scale's ends.
 */
export function scaleColumns(bands: { from: number; to: number; width?: number }[]): {
  columns: ScaleColumn[];
  positionOf: (value: number) => number;
} {
  const weights = bands.every((b) => (b.width ?? 0) > 0)
    ? bands.map((b) => b.width ?? 0)
    : bands.map((b) => Math.max(0, b.to - b.from));
  const total = weights.reduce((sum, w) => sum + w, 0);
  let reached = 0;
  const columns = weights.map((w) => {
    const span = total > 0 ? (w / total) * 100 : 0;
    const column = { start: reached, span };
    reached += span;
    return column;
  });

  const positionOf = (value: number): number => {
    const first = bands[0];
    const last = bands[bands.length - 1];
    if (!first || !last || total <= 0) return 0;
    if (value <= first.from) return 0;
    if (value >= last.to) return 100;
    // Bands meet at their bounds, so a value on a boundary takes the lower
    // band's top, which is the same point as the next band's start.
    const index = bands.findIndex((b) => value <= b.to);
    const band = bands[index];
    const column = columns[index];
    if (!band || !column) return 100;
    const range = band.to - band.from;
    const within = range > 0 ? Math.max(0, (value - band.from) / range) : 0;
    return round(column.start + within * column.span);
  };

  return { columns, positionOf };
}

/**
 * The axis as one flat block of colour per band, with a hard edge where the
 * next band starts: a blend made neighbouring bands hard to tell apart. Each
 * band's colour is stopped at both its edges, which is what makes the edges
 * hard. A band with no tone takes the neutral colour rather than dropping its
 * stops, which would silently stretch its neighbour across the gap.
 */
export function axisRamp(bands: { start: number; end: number; tone?: ScoreBandTone }[]): string {
  return bands
    .map((b) => {
      const colour = `var(--color-report-${b.tone ? `tone-${b.tone}` : "denominator"})`;
      return `${colour} ${round(b.start)}%, ${colour} ${round(b.end)}%`;
    })
    .join(", ");
}

/**
 * Which axis value labels can be printed without running into each other.
 * Candidates are taken in priority order (the caller lists the ones that must
 * stay first); one is dropped when it would sit within `minGapPercent` of a
 * label already kept, so two close scores never print as one smeared number.
 */
export function visibleAxisLabels<T extends { position: number }>(
  candidates: T[],
  minGapPercent: number
): T[] {
  const kept: T[] = [];
  for (const c of candidates) {
    if (kept.every((k) => Math.abs(k.position - c.position) >= minGapPercent)) kept.push(c);
  }
  return kept;
}

/**
 * `items` arranged by `ids`, first to last. Items whose id is not listed keep
 * their given order after the ones that are, so an incomplete list degrades to
 * "listed first" rather than dropping anything. Omitted ids means no change.
 */
export function orderByIds<T extends { id: string }>(items: T[], ids: string[] | undefined): T[] {
  if (!ids) return items;
  const rank = (item: T) => {
    const at = ids.indexOf(item.id);
    return at === -1 ? ids.length : at;
  };
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
    .map(({ item }) => item);
}
